// ===========================================
// Registration Fee Fields
// ===========================================
// A controlled field group (amount / date / method / reference / notes) for
// recording or amending a manual (offline) registration payment. Shared by
// RecordRegistrationPaymentDialog and ConvertToStudentDialog's "Paid another
// way" mode. No balance cap — a registration fee is one-off, not partial.
// md/plan/family-payment-history.md Task 4.3.

import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import type { PaymentMethod } from '../../../api/invoices.api';

export interface RegistrationFeeFormValues {
  amount: string;
  method: Exclude<PaymentMethod, 'STRIPE'>;
  /** School-local calendar day (YYYY-MM-DD). */
  paidAt: string;
  reference: string;
  notes: string;
}

const OFFLINE_PAYMENT_METHODS: { value: Exclude<PaymentMethod, 'STRIPE'>; label: string }[] = [
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CHECK', label: 'Cheque' },
  { value: 'OTHER', label: 'Other' },
];

/** Up to 2 decimal places (a bare "10." or "10" is fine; "10.555" is not). */
const AMOUNT_FORMAT = /^\d+(\.\d{1,2})?$/;

/** Amount > 0, <= 100000, at most 2 decimal places; date matches YYYY-MM-DD and is not in the future. */
export function isRegistrationFeeValid(values: RegistrationFeeFormValues, todayKey: string): boolean {
  const amount = parseFloat(values.amount);
  const amountValid =
    Number.isFinite(amount) && amount > 0 && amount <= 100000 && AMOUNT_FORMAT.test(values.amount.trim());
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(values.paidAt) && values.paidAt <= todayKey;
  return amountValid && dateValid;
}

interface RegistrationFeeFieldsProps {
  values: RegistrationFeeFormValues;
  onChange: (values: RegistrationFeeFormValues) => void;
  todayKey: string;
  disabled?: boolean;
}

export default function RegistrationFeeFields({
  values,
  onChange,
  todayKey,
  disabled = false,
}: RegistrationFeeFieldsProps) {
  const amount = parseFloat(values.amount) || 0;
  const isAmountValid =
    values.amount !== '' && amount > 0 && amount <= 100000 && AMOUNT_FORMAT.test(values.amount.trim());
  const isDateValid =
    values.paidAt === '' || (/^\d{4}-\d{2}-\d{2}$/.test(values.paidAt) && values.paidAt <= todayKey);

  const set = (patch: Partial<RegistrationFeeFormValues>) => onChange({ ...values, ...patch });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        fullWidth
        required
        type="number"
        label="Amount"
        value={values.amount}
        onChange={(e) => set({ amount: e.target.value })}
        disabled={disabled}
        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        inputProps={{ min: 0, step: 0.01, max: 100000 }}
        error={values.amount !== '' && !isAmountValid}
        helperText={
          values.amount !== '' && !isAmountValid
            ? 'Enter an amount between $0.01 and $100,000. Up to 2 decimal places'
            : undefined
        }
      />

      <TextField
        fullWidth
        required
        type="date"
        label="Date received"
        value={values.paidAt}
        onChange={(e) => set({ paidAt: e.target.value })}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        inputProps={{ max: todayKey }}
        error={values.paidAt !== '' && !isDateValid}
        helperText={
          values.paidAt !== ''
            ? !isDateValid
              ? 'Date cannot be in the future'
              : 'The day the money was received'
            : 'Choose the date the money was received'
        }
      />

      <FormControl fullWidth required disabled={disabled}>
        <InputLabel>Payment Method</InputLabel>
        <Select
          value={values.method}
          label="Payment Method"
          onChange={(e: SelectChangeEvent) => set({ method: e.target.value as RegistrationFeeFormValues['method'] })}
        >
          {OFFLINE_PAYMENT_METHODS.map((pm) => (
            <MenuItem key={pm.value} value={pm.value}>
              {pm.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        label="Reference"
        value={values.reference}
        onChange={(e) => set({ reference: e.target.value })}
        disabled={disabled}
        helperText="Optional: bank transfer ID, cheque number, etc."
        inputProps={{ maxLength: 100 }}
      />

      <TextField
        fullWidth
        label="Notes"
        value={values.notes}
        onChange={(e) => set({ notes: e.target.value })}
        disabled={disabled}
        multiline
        rows={2}
        placeholder="Optional notes about this payment"
        inputProps={{ maxLength: 500 }}
      />
    </Box>
  );
}
